import axios from 'axios';
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken } from 'firebase/messaging';

const PUSH_NOTIFICATION_TOKEN_KEY = 'push_notification_token';

// Initialize Firebase with environment variables
const firebaseConfig = {
	apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
	authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
	projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
	storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
	messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
	appId: process.env.REACT_APP_FIREBASE_APP_ID,
	vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
};

// Initialize Firebase app
const firebaseApp = initializeApp(firebaseConfig);
const messaging = getMessaging(firebaseApp);

export class PushNotificationService {
	private static instance: PushNotificationService;
	private baseUrl: string;

	private constructor() {
		this.baseUrl = process.env.REACT_APP_API_URL || '';
	}

	public static getInstance(): PushNotificationService {
		if (!PushNotificationService.instance) {
			PushNotificationService.instance = new PushNotificationService();
		}
		return PushNotificationService.instance;
	}

	public async getToken(): Promise<string | null> {
		return localStorage.getItem(PUSH_NOTIFICATION_TOKEN_KEY);
	}

	private setToken(token: string): void {
		localStorage.setItem(PUSH_NOTIFICATION_TOKEN_KEY, token);
	}

	public async generateToken(): Promise<string> {
		// Request permission for notifications
		const permission = await Notification.requestPermission();
		if (permission !== 'granted') {
			throw new Error('Notification permission denied');
		}

		try {
			const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');

			// Wait for the service worker to be ready
			await navigator.serviceWorker.ready;

			// Pass Firebase config to service worker - make sure it's active
			if (registration.active) {
				registration.active.postMessage({
					type: 'FIREBASE_CONFIG',
					config: firebaseConfig,
				});
			} else {
				// If not active yet, set up a listener for when it activates
				navigator.serviceWorker.addEventListener('controllerchange', () => {
					registration.active?.postMessage({
						type: 'FIREBASE_CONFIG',
						config: firebaseConfig,
					});
				});
			}

			// Remove vapidKey if you don't have one yet
			const token = await getToken(messaging, {
				serviceWorkerRegistration: registration,
			});

			if (token) {
				this.setToken(token);
				await this.sendTokenToBackend(token);
				return token;
			} else {
				throw new Error('Failed to generate token');
			}
		} catch (error) {
			console.error('Error generating token:', error);
			throw error;
		}
	}

	private async sendTokenToBackend(token: string): Promise<void> {
		try {
			await axios.post(`${this.baseUrl}/push-notification-token/v1`, {
				token,
			});
		} catch (error) {
			console.error('Error sending token to backend:', error);
			throw error;
		}
	}

	public async initializePushNotifications(): Promise<void> {
		try {
			let token = await this.getToken();

			if (!token) {
				token = await this.generateToken();
			} else {
				await this.sendTokenToBackend(token);
			}
		} catch (error) {
			console.error('Error initializing push notifications:', error);
			throw error;
		}
	}
}
